import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { 
  Car, Home as HomeIcon, Heart, Briefcase, Shield, Clock, 
  ChevronRight, Phone, ArrowRight, CheckCircle, FileText,
  Building2, Users, Award, MapPin, Zap
} from 'lucide-react';

const services = [
  {
    icon: Car,
    title: 'Automobile',
    description: 'Tous véhicules, tous conducteurs. Formules adaptées à votre profil.',
    color: 'from-blue-500 to-blue-600',
    link: '/quote?type=auto'
  },
  {
    icon: HomeIcon,
    title: 'Habitation',
    description: 'Propriétaire ou locataire, protégez votre logement et vos biens.',
    color: 'from-emerald-500 to-emerald-600',
    link: '/quote?type=habitation'
  },
  {
    icon: Heart,
    title: 'Santé & Prévoyance',
    description: 'Complémentaire santé et protection de vos revenus.',
    color: 'from-rose-500 to-rose-600',
    link: '/quote?type=sante'
  },
  {
    icon: Briefcase,
    title: 'Professionnels',
    description: 'RC Pro, Multirisque, Décennale. Solutions sur mesure.',
    color: 'from-amber-500 to-amber-600',
    link: '/quote?type=pro'
  },
  {
    icon: Building2,
    title: 'Garantie Décennale',
    description: 'Artisans et constructeurs, sécurisez vos chantiers.',
    color: 'from-violet-500 to-violet-600',
    link: '/quote?type=decennale'
  },
  {
    icon: FileText,
    title: 'Carte Grise',
    description: 'Service agréé. Immatriculation rapide et simplifiée.',
    color: 'from-slate-500 to-slate-600',
    link: '/carte-grise'
  }
];

const stats = [
  { value: '2024', label: 'Création du cabinet', icon: Award },
  { value: '100%', label: 'Indépendant', icon: Shield },
  { value: '24/7', label: 'Assurance temporaire', icon: Clock },
  { value: 'ORIAS', label: 'Immatriculé', icon: CheckCircle }
];

const partners = [
  'AXA', 'Allianz', 'Generali', 'SwissLife', 'MAIF', 'April', 'Solly Azar', 'Néoliane'
];

export const Home = () => {
  return (
    <>
      <Helmet>
        <title>Cabinet CAMBC Conseils | Courtier Assurance Moissac 82</title>
        <meta name="description" content="Courtier en assurance à Moissac (82). Cabinet CAMBC Conseils : auto, habitation, santé, professionnels, décennale. Conseil personnalisé et devis gratuit." />
        <meta name="keywords" content="courtier assurance 82, courtier assurance Moissac, assurance Tarn-et-Garonne, CAMBC Conseils, assurance pro, décennale" />
        <link rel="canonical" href="https://www.cambc-conseils.fr/" />
      </Helmet>

      <div className="min-h-screen bg-[#F9F8F6]">
        {/* Hero Section */}
        <section className="relative min-h-[90vh] flex items-center overflow-hidden">
          {/* Background */}
          <div className="absolute inset-0 z-0">
            <div className="absolute inset-0 bg-gradient-to-br from-[#0F2137] via-[#1E3A5F] to-[#0F2137]"></div>
            {/* Subtle pattern */}
            <div className="absolute inset-0 opacity-[0.03]" style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M11 18c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm48 25c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm-43-7c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm63 31c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM34 90c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm56-76c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM12 86c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm28-65c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm23-11c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-6 60c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm29 22c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zM32 63c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm57-13c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-9-21c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM60 91c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM35 41c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2z' fill='%23ffffff' fill-opacity='1' fill-rule='evenodd'/%3E%3C/svg%3E")`
            }}></div>
            {/* Gold accent line */}
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-[#C5A059] to-transparent"></div>
          </div>
          
          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              {/* Left content */}
              <div>
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#C5A059]/10 border border-[#C5A059]/20 rounded-full text-[#C5A059] text-sm font-medium mb-8">
                  <Shield size={16} />
                  <span>ORIAS n° 24000879 — Courtier indépendant</span>
                </div>
                
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white mb-6 leading-[1.1] tracking-tight">
                  L'assurance qui vous
                  <span className="block text-[#C5A059]">ressemble</span>
                </h1>
                
                <p className="text-lg sm:text-xl text-slate-300 mb-10 leading-relaxed max-w-xl">
                  Cabinet de courtage à Moissac, nous négocions pour vous les meilleures garanties 
                  auprès des plus grands assureurs. Particuliers et professionnels, bénéficiez 
                  d'un conseil expert et personnalisé.
                </p>
                
                <div className="flex flex-col sm:flex-row gap-4 mb-12">
                  <Link to="/quote" data-testid="hero-cta-devis">
                    <Button size="lg" className="bg-[#C5A059] hover:bg-[#B08A45] text-white font-semibold text-base px-8 py-6 rounded-xl shadow-lg shadow-[#C5A059]/25 transition-all duration-300 hover:shadow-xl hover:shadow-[#C5A059]/30 hover:-translate-y-0.5">
                      Demander un devis gratuit
                      <ArrowRight className="ml-2" size={18} />
                    </Button>
                  </Link>
                  <Link to="/assurance-temporaire" data-testid="hero-cta-temporaire">
                    <Button size="lg" variant="outline" className="border-2 border-white/20 text-white hover:bg-white/10 font-medium text-base px-8 py-6 rounded-xl backdrop-blur-sm transition-all duration-300">
                      <Clock className="mr-2" size={18} />
                      Assurance 24h/24
                    </Button>
                  </Link>
                </div>

                {/* Contact info */}
                <div className="flex flex-wrap items-center gap-6 text-white/70">
                  <a href="tel:0582819120" className="flex items-center gap-2 hover:text-[#C5A059] transition-colors">
                    <Phone size={18} />
                    <span className="font-medium">05 82 81 91 20</span>
                  </a>
                  <div className="flex items-center gap-2">
                    <MapPin size={18} />
                    <span>Moissac, Tarn-et-Garonne</span>
                  </div>
                </div>
              </div>

              {/* Right - Stats cards */}
              <div className="hidden lg:grid grid-cols-2 gap-4">
                {stats.map((stat, index) => (
                  <div 
                    key={index}
                    className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 hover:bg-white/10 transition-all duration-300"
                  >
                    <stat.icon size={24} className="text-[#C5A059] mb-4" />
                    <div className="text-3xl font-bold text-white mb-1">{stat.value}</div>
                    <div className="text-slate-400 text-sm">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Partners Banner */}
        <section className="bg-white border-y border-slate-200 py-8">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-wrap items-center justify-center gap-x-12 gap-y-4">
              <span className="text-sm text-slate-500 font-medium">Nos partenaires :</span>
              {partners.map((partner, index) => (
                <span key={index} className="text-slate-400 font-semibold text-lg tracking-wide">
                  {partner}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* Services Section */}
        <section className="py-24 bg-[#F9F8F6]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#C5A059] mb-4 block">
                Nos expertises
              </span>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0F2137] mb-6 tracking-tight">
                Solutions d'assurance complètes
              </h2>
              <p className="text-lg text-slate-600 max-w-2xl mx-auto leading-relaxed">
                Particuliers et professionnels, nous analysons vos besoins pour vous proposer 
                les garanties adaptées au meilleur tarif.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {services.map((service, index) => (
                <Link 
                  key={index} 
                  to={service.link}
                  data-testid={`service-card-${service.title.toLowerCase()}`}
                  className="group"
                >
                  <Card className="h-full bg-white border-0 shadow-sm hover:shadow-xl transition-all duration-500 rounded-2xl overflow-hidden group-hover:-translate-y-1">
                    <CardContent className="p-8">
                      <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${service.color} flex items-center justify-center mb-6 shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                        <service.icon size={24} className="text-white" />
                      </div>
                      <h3 className="text-xl font-bold text-[#0F2137] mb-3 group-hover:text-[#1E3A5F] transition-colors">
                        {service.title}
                      </h3>
                      <p className="text-slate-600 leading-relaxed mb-4">
                        {service.description}
                      </p>
                      <div className="flex items-center text-[#C5A059] font-medium text-sm group-hover:gap-2 transition-all">
                        <span>En savoir plus</span>
                        <ChevronRight size={16} className="group-hover:translate-x-1 transition-transform" />
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section - Temporary Insurance */}
        <section className="py-24 bg-[#0F2137] relative overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 right-0 w-96 h-96 bg-[#C5A059] rounded-full blur-3xl"></div>
            <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#1E3A5F] rounded-full blur-3xl"></div>
          </div>
          
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <div>
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#C5A059]/20 rounded-full text-[#C5A059] text-sm font-medium mb-6">
                  <Zap size={16} />
                  <span>Service disponible 24h/24 — 7j/7</span>
                </div>
                <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6 tracking-tight">
                  Assurance temporaire
                  <span className="block text-[#C5A059]">immédiate</span>
                </h2>
                <p className="text-lg text-slate-300 mb-8 leading-relaxed">
                  Besoin d'une assurance auto pour quelques jours ? Souscrivez en ligne 
                  24h/24 et recevez votre carte verte instantanément. Import, export, 
                  convoyage, essai véhicule...
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <Link to="/assurance-temporaire" data-testid="cta-temporaire-btn">
                    <Button size="lg" className="bg-[#C5A059] hover:bg-[#B08A45] text-white font-semibold px-8 py-6 rounded-xl">
                      Souscrire maintenant
                      <ArrowRight className="ml-2" size={18} />
                    </Button>
                  </Link>
                  <a href="tel:0582819120">
                    <Button size="lg" variant="outline" className="border-2 border-white/20 text-white hover:bg-white/10 px-8 py-6 rounded-xl">
                      <Phone className="mr-2" size={18} />
                      Nous appeler
                    </Button>
                  </a>
                </div>
              </div>
              
              <div className="hidden lg:block">
                <div className="grid grid-cols-2 gap-4">
                  {[
                    { title: 'De 1 à 90 jours', desc: 'Durée flexible' },
                    { title: 'Carte verte immédiate', desc: 'Par email' },
                    { title: 'Tous véhicules', desc: 'Auto, moto, utilitaire' },
                    { title: 'Sans engagement', desc: 'Résiliation auto' }
                  ].map((item, i) => (
                    <div key={i} className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6">
                      <CheckCircle size={20} className="text-[#C5A059] mb-3" />
                      <div className="text-white font-semibold mb-1">{item.title}</div>
                      <div className="text-slate-400 text-sm">{item.desc}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Why Choose Us */}
        <section className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <div>
                <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#C5A059] mb-4 block">
                  Pourquoi nous choisir
                </span>
                <h2 className="text-3xl sm:text-4xl font-bold text-[#0F2137] mb-6 tracking-tight">
                  Un courtier à vos côtés
                </h2>
                <p className="text-lg text-slate-600 mb-8 leading-relaxed">
                  En tant que courtier indépendant, nous travaillons exclusivement dans votre 
                  intérêt. Nous comparons les offres de nombreux assureurs pour vous proposer 
                  le meilleur rapport qualité/prix.
                </p>
                
                <div className="space-y-6">
                  {[
                    { title: 'Conseil personnalisé', desc: 'Analyse de vos besoins et recommandations adaptées' },
                    { title: 'Tarifs négociés', desc: 'Accès aux conditions courtiers et remises exclusives' },
                    { title: 'Accompagnement sinistre', desc: 'Nous gérons vos dossiers et défendons vos intérêts' },
                    { title: 'Proximité', desc: 'Cabinet basé à Moissac, disponible et réactif' }
                  ].map((item, i) => (
                    <div key={i} className="flex gap-4">
                      <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#F5EFE6] flex items-center justify-center">
                        <CheckCircle size={20} className="text-[#C5A059]" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-[#0F2137] mb-1">{item.title}</h4>
                        <p className="text-slate-600 text-sm">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="relative">
                <div className="aspect-square rounded-3xl bg-gradient-to-br from-[#0F2137] to-[#1E3A5F] p-8 shadow-2xl">
                  <div className="h-full rounded-2xl bg-white/5 backdrop-blur-sm border border-white/10 p-8 flex flex-col justify-center">
                    <div className="text-center">
                      <div className="w-20 h-20 rounded-full bg-[#C5A059]/20 flex items-center justify-center mx-auto mb-6">
                        <Shield size={40} className="text-[#C5A059]" />
                      </div>
                      <h3 className="text-2xl font-bold text-white mb-4">Cabinet CAMBC Conseils</h3>
                      <p className="text-slate-300 mb-6">Courtier en assurance<br />ORIAS n° 24000879</p>
                      <div className="text-[#C5A059] font-semibold">
                        46 bd Pierre Delbrel<br />82200 Moissac
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="py-20 bg-[#F9F8F6]">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#0F2137] mb-6">
              Prêt à mieux protéger ce qui compte ?
            </h2>
            <p className="text-lg text-slate-600 mb-8">
              Contactez-nous pour un devis gratuit et sans engagement. 
              Notre équipe vous répond sous 24h.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/quote" data-testid="final-cta-devis">
                <Button size="lg" className="bg-[#C5A059] hover:bg-[#B08A45] text-white font-semibold px-10 py-6 rounded-xl shadow-lg">
                  Demander un devis gratuit
                  <ArrowRight className="ml-2" size={18} />
                </Button>
              </Link>
              <Link to="/contact" data-testid="final-cta-contact">
                <Button size="lg" variant="outline" className="border-2 border-[#0F2137] text-[#0F2137] hover:bg-[#0F2137] hover:text-white px-10 py-6 rounded-xl">
                  Nous contacter
                </Button>
              </Link>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};
